/* global __APP_CONFIG__ */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useBranding } from '../context/BrandingContext'
import { usePermissions } from '../context/PermissionContext'
import { useTheme } from '../context/ThemeContext'
import useSystemHealth from '../hooks/useSystemHealth'
import useAppNavigation, { isNavigationItemActive } from '../navigation/useAppNavigation'
import DisplayControls from '../components/DisplayControls'
import { BRANDING_LOGO_CHANGED_EVENT, PUBLIC_BRANDING_LOGO_URL } from '../publicAssets'
import { OpsButton, OpsSheet, StateLabel, ViewState } from '../ui'

export function firstDestination(section) {
  return section.homePath || section.items.find((navigationItem) => navigationItem.path)?.path || '/'
}

export function findNavigationContext(sections, pathname) {
  for (const section of sections) {
    for (const navigationItem of section.items) {
      if (navigationItem.children) {
        const child = navigationItem.children.find((candidate) => isNavigationItemActive(candidate, pathname))
        if (child) return { section, item: navigationItem, child }
      }
      if (isNavigationItemActive(navigationItem, pathname)) return { section, item: navigationItem, child: null }
    }
    if (section.homePath === pathname) {
      return {
        section,
        item: { id: `${section.id}-overview`, label: 'Overview', path: section.homePath },
        child: null,
      }
    }
  }
  if (pathname === '/') return { section: null, item: { label: 'Operational overview', path: '/' }, child: null }
  return {
    section: null,
    item: {
      label: pathname.split('/').filter(Boolean).at(-1)?.replaceAll('-', ' ') || 'Workspace',
      path: pathname,
    },
    child: null,
  }
}

export function Wordmark({ name, subtitle }) {
  const productName = name || 'GAEZLA'
  const productSubtitle = subtitle || 'Operations'
  const [logoVersion, setLogoVersion] = useState(null)

  useEffect(() => {
    const refreshLogo = () => setLogoVersion((currentVersion) => (
      Math.max(Date.now(), (currentVersion || 0) + 1)
    ))
    window.addEventListener(BRANDING_LOGO_CHANGED_EVENT, refreshLogo)
    return () => window.removeEventListener(BRANDING_LOGO_CHANGED_EVENT, refreshLogo)
  }, [])

  const logoSrc = logoVersion
    ? `${PUBLIC_BRANDING_LOGO_URL}?v=${logoVersion}`
    : PUBLIC_BRANDING_LOGO_URL

  return (
    <Link className="ops-wordmark" to="/" aria-label={`${productName} ${productSubtitle} home`}>
      <img className="ops-wordmark__logo" src={logoSrc} alt="" />
      <span className="ops-wordmark__copy"><strong>{productName}</strong><small>{productSubtitle}</small></span>
    </Link>
  )
}

function DismissibleMenu({ className, children }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const handlePointerDown = (event) => {
      const menu = menuRef.current
      if (menu?.open && !menu.contains(event.target)) menu.open = false
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  return <details className={className} ref={menuRef}>{children}</details>
}

function HealthMenu() {
  const { health, loading, error } = useSystemHealth()
  const state = error ? 'unavailable' : health.state === 'degraded' ? 'critical' : 'observed'
  const label = error
    ? 'Health unavailable'
    : loading && health.counts.total === 0
      ? 'Reading health'
      : `${health.counts.healthy}/${health.counts.total} observed healthy`

  return (
    <DismissibleMenu className="ops-menu ops-health-menu">
      <summary aria-label={`System health: ${label}`}><StateLabel state={state} label={label} /></summary>
      <div className="ops-menu__panel ops-health-menu__panel">
        <header>
          <p className="ops-kicker">Observed systems</p>
          <h2>System health</h2>
          <p>{error ? 'The health endpoint could not be reached.' : 'Current reported state for configured systems.'}</p>
        </header>
        {health.groups.length === 0 && !error && <p className="ops-menu__empty">No systems are configured.</p>}
        {health.groups.map((group) => (
          <section key={group.key}>
            <h3>{group.label}</h3>
            {group.items.map((healthItem, index) => (
              healthItem.href ? (
                <Link key={`${group.key}-${index}`} to={healthItem.href}>
                  <span>{healthItem.name}<small>{healthItem.reason || healthItem.detail}</small></span>
                  <StateLabel state={healthItem.state === 'degraded' ? 'critical' : 'observed'} />
                </Link>
              ) : (
                <div key={`${group.key}-${index}`} className="ops-menu__row">
                  <span>{healthItem.name}<small>{healthItem.reason || healthItem.detail}</small></span>
                  <StateLabel state={healthItem.state === 'degraded' ? 'critical' : 'observed'} />
                </div>
              )
            ))}
          </section>
        ))}
        <footer className="ops-provenance">
          {health.generated_at ? `Observed ${new Date(health.generated_at).toLocaleString()}` : 'Observation time unavailable'}
        </footer>
      </div>
    </DismissibleMenu>
  )
}

function AccountMenu() {
  const { instance } = useMsal()
  const navigate = useNavigate()
  const { appRoles, userEmail, isGlobalAdmin, refreshing, refreshPermissions } = usePermissions()
  const initials = useMemo(() => {
    const local = userEmail?.split('@')[0] || ''
    const parts = local.split('.')
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : local.slice(0, 2).toUpperCase() || '?'
  }, [userEmail])

  return (
    <DismissibleMenu className="ops-menu ops-account-menu">
      <summary aria-label="Account menu"><span className={`ops-avatar${isGlobalAdmin() ? ' ops-avatar--admin' : ''}`}>{initials}</span></summary>
      <div className="ops-menu__panel ops-account-menu__panel">
        <header>
          <p className="ops-kicker">Current operator</p>
          <h2>{instance.getActiveAccount?.()?.name || userEmail || 'Account unavailable'}</h2>
          {userEmail && <p>{userEmail}</p>}
        </header>
        <div className="ops-account-menu__roles">
          {appRoles.length ? appRoles.map((role) => <span key={role}>{role}</span>) : <span>No roles reported</span>}
        </div>
        <nav aria-label="Account actions">
          <button type="button" onClick={refreshPermissions} disabled={refreshing}>{refreshing ? 'Refreshing permissions' : 'Refresh permissions'}</button>
          <button type="button" onClick={() => navigate('/me/settings')}>My settings</button>
          <button type="button" onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}>Log out</button>
        </nav>
      </div>
    </DismissibleMenu>
  )
}

function DisclosureGlyph() {
  return (
    <svg className="ops-disclosure" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RouteTrail({ context }) {
  return (
    <>
      <span>{context.section?.label || 'Operations'}</span>
      <b aria-hidden="true">/</b>
      {context.child && (
        <>
          <span>{context.item.label}</span>
          <b aria-hidden="true">/</b>
        </>
      )}
      <strong>{context.child?.label || context.item.label}</strong>
    </>
  )
}

export function locationLabel(context) {
  return [
    context.section?.label || 'Operations',
    context.child ? context.item.label : null,
    context.child?.label || context.item.label,
  ].filter(Boolean).join(' / ')
}

/* One navigation sheet for every tier. It opens from the location control and lists every
   permitted destination in the same fixed order as the domain nav. The order never changes;
   only the "You are here" mark and the current-page mark move. */
function NavigationSheet({ sections, currentSectionId, pathname, loading, open, onClose, triggerRef }) {
  return (
    <OpsSheet open={open} title="Menu" onClose={onClose} returnFocusRef={triggerRef} anchor="start" className="ops-menu-launcher">
      {loading ? (
        <ViewState kind="loading" title="Reading available work" detail="Checking current permissions, entitlements, and configured integrations." />
      ) : sections.length === 0 ? (
        <ViewState kind="empty" title="No destinations available" detail="No operational destination is currently available to this operator." />
      ) : <nav aria-label="All destinations">
        {sections.map((section) => {
          const current = section.id === currentSectionId
          return (
            <section key={section.id} className={current ? 'is-current' : undefined}>
              <header>
                <p className="ops-kicker">
                  {section.label}
                  {current && <small className="ops-launcher-here">You are here</small>}
                </p>
                {section.homePath && (
                  <Link to={section.homePath} onClick={onClose} aria-current={pathname === section.homePath ? 'page' : undefined}>Open overview</Link>
                )}
              </header>
              <div>
                {section.items.map((navigationItem) => {
                  const children = navigationItem.children || []
                  const itemCurrent = children.length === 0 && isNavigationItemActive(navigationItem, pathname)
                  return (
                    <div key={navigationItem.id} className="ops-launcher-item">
                      {navigationItem.path ? (
                        <Link to={navigationItem.path} onClick={onClose} aria-current={itemCurrent ? 'page' : undefined}>
                          <span>{navigationItem.label}</span>
                          {navigationItem.count != null && <small>{navigationItem.count}</small>}
                        </Link>
                      ) : <span>{navigationItem.label}</span>}
                      {children.length > 0 && (
                        <div className="ops-launcher-item__children">
                          {children.map((child) => {
                            const childCurrent = isNavigationItemActive(child, pathname)
                            return (
                              <Link key={child.id} to={child.path} onClick={onClose} aria-current={childCurrent ? 'page' : undefined}>
                                {child.label}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )
        })}
      </nav>}
    </OpsSheet>
  )
}

function DesktopSectionNavigation({ items, pathname, sectionLabel }) {
  return (
    <nav className="ops-task-nav" aria-label={`${sectionLabel} destinations`}>
      {items.map((navigationItem) => {
        const children = navigationItem.children || []
        const activeChild = children.find((child) => isNavigationItemActive(child, pathname))
        const groupActive = Boolean(activeChild) || isNavigationItemActive(navigationItem, pathname)
        const parentClassName = children.length > 0
          ? (groupActive ? 'is-group-current' : undefined)
          : (groupActive ? 'is-current' : undefined)

        return (
          <div key={navigationItem.id} className={`ops-task-group${groupActive ? ' is-expanded' : ''}`}>
            {navigationItem.path ? (
              <Link
                to={navigationItem.path}
                className={parentClassName}
                aria-current={children.length === 0 && groupActive ? 'page' : undefined}
              >
                {navigationItem.label}
              </Link>
            ) : <span className={parentClassName}>{navigationItem.label}</span>}
            {groupActive && children.length > 0 && (
              <div className="ops-task-nav__children">
                {children.map((child) => {
                  const current = isNavigationItemActive(child, pathname)
                  return (
                    <Link
                      key={child.id}
                      to={child.path}
                      className={current ? 'is-current' : undefined}
                      aria-current={current ? 'page' : undefined}
                    >
                      {child.label}
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

export default function GaezlaHeader() {
  const { pathname } = useLocation()
  const { loading: navigationLoading, sections } = useAppNavigation()
  const { theme, toggleTheme } = useTheme()
  const branding = useBranding()
  const [navigationOpen, setNavigationOpen] = useState(false)
  const navigationButtonRef = useRef(null)
  const closeNavigation = useCallback(() => setNavigationOpen(false), [])
  const context = findNavigationContext(sections, pathname)
  const activeSectionItems = context.section?.items || []
  const config = window.__APP_CONFIG__ || __APP_CONFIG__

  return (
    <>
      {config?.debugMode && <div className="ops-debug-banner">Debug environment: {config.environment}</div>}
      <header className="ops-header">
        <div className="ops-header__primary">
          <Wordmark name={branding.name} subtitle={branding.subtitle} />
          <nav className="ops-domain-nav" aria-label="Operational domains">
            {sections.map((section) => (
              <Link
                key={section.id}
                to={firstDestination(section)}
                className={context.section?.id === section.id ? 'is-current' : undefined}
              >
                {section.label}
              </Link>
            ))}
          </nav>
          <div className="ops-header__tools">
            <HealthMenu />
            <OpsButton tone="quiet" className="ops-header__utility" onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? 'Light' : 'Dark'}
            </OpsButton>
            <AccountMenu />
          </div>
        </div>
        <div className="ops-header__context">
          {/* The location is the menu: where you are, and the way to everywhere else. */}
          <button
            ref={navigationButtonRef}
            className="ops-location-trigger"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={navigationOpen}
            aria-label={`Menu: ${locationLabel(context)}`}
            onClick={() => setNavigationOpen(true)}
          >
            <span className="ops-route-trail"><RouteTrail context={context} /></span>
            <DisclosureGlyph />
          </button>
          {activeSectionItems.length > 0 && (
            <DesktopSectionNavigation items={activeSectionItems} pathname={pathname} sectionLabel={context.section.label} />
          )}
          <div className="ops-context-actions">
            <DisplayControls />
          </div>
        </div>
      </header>
      <NavigationSheet
        sections={sections}
        currentSectionId={context.section?.id}
        pathname={pathname}
        loading={navigationLoading}
        open={navigationOpen}
        onClose={closeNavigation}
        triggerRef={navigationButtonRef}
      />
    </>
  )
}
